import classNames from "classnames";
import { DashboardUtils } from "../../../dashboard_utils";
import { SeshPrimaryButton } from "../base_components/sesh_primary_button";

export type LicenseType = "premium" | "pro";

export interface LicenseTypeSelectionModalProps {
	open: boolean;
	onClose: () => void;
	onSelect: (type: LicenseType) => void;
	premiumAvailable: number;
	premiumUsed: number;
	proAvailable: number;
	proUsed: number;
	guildName: string;
}

export function LicenseTypeSelectionModal(
	props: LicenseTypeSelectionModalProps
) {
	const {
		open,
		onClose,
		onSelect,
		premiumAvailable,
		premiumUsed,
		proAvailable,
		proUsed,
		guildName,
	} = props;

	if (!open) {
		return null;
	}

	const premiumRemaining = premiumAvailable - premiumUsed;
	const proRemaining = proAvailable - proUsed;

	return (
		<DashboardUtils.SeshModal
			is_active={open}
			onClose={onClose}
			title="Choose License Type"
			subtitle={`Select which license to use for ${guildName}`}
			content={
				<div className="mt-4 flex flex-col gap-4">
					{/* Premium Option */}
					<button
						onClick={() => onSelect("premium")}
						disabled={premiumRemaining <= 0}
						className={classNames(
							"flex items-center gap-4 rounded-lg border p-4 text-left transition-colors",
							premiumRemaining > 0
								? "border-sesh-primary/50 bg-sesh-primary/10 hover:border-sesh-primary hover:bg-sesh-primary/20"
								: "cursor-not-allowed border-slate-600 bg-slate-800/30 opacity-50"
						)}
					>
						<div className="flex h-12 w-12 items-center justify-center rounded-full bg-sesh-primary/20">
							<i className="fas fa-gem fa-lg text-sesh-primary"></i>
						</div>
						<div className="flex-1">
							<div className="flex items-center gap-2">
								<span className="text-lg font-bold text-white">
									Premium
								</span>
								<span className="text-sm text-slate-400">
									({premiumUsed}/{premiumAvailable} used)
								</span>
							</div>
							<p className="text-sm text-slate-300">
								Repeating events, custom RSVP options, full
								GCal sync, and more
							</p>
						</div>
						{premiumRemaining > 0 && (
							<i className="fas fa-chevron-right text-sesh-primary"></i>
						)}
					</button>

					{/* Pro Option */}
					<button
						onClick={() => onSelect("pro")}
						disabled={proRemaining <= 0}
						className={classNames(
							"flex items-center gap-4 rounded-lg border p-4 text-left transition-colors",
							proRemaining > 0
								? "border-amber-500/50 bg-amber-500/10 hover:border-amber-500 hover:bg-amber-500/20"
								: "cursor-not-allowed border-slate-600 bg-slate-800/30 opacity-50"
						)}
					>
						<div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/20">
							<i className="fas fa-crown fa-lg text-amber-300"></i>
						</div>
						<div className="flex-1">
							<div className="flex items-center gap-2">
								<span className="text-lg font-bold text-white">
									sesh Pro
								</span>
								<span className="text-sm text-slate-400">
									({proUsed}/{proAvailable} used)
								</span>
							</div>
							<p className="text-sm text-slate-300">
								Everything in Premium plus API access, custom
								branding, and webhooks
							</p>
						</div>
						{proRemaining > 0 && (
							<i className="fas fa-chevron-right text-amber-300"></i>
						)}
					</button>

					<p className="text-center text-xs text-slate-500">
						You can change or deactivate this later from server
						settings
					</p>
				</div>
			}
		/>
	);
}
